'use client';

import { useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import type { CallbackPayload, ConfirmCallback } from './type';

type CallbackSubmit = {
  submitting: boolean;
  failed: boolean;
  /** Resolves to the payload that was booked, or `null` when the endpoint refused it. */
  submit: (payload: CallbackPayload) => Promise<CallbackPayload | null>;
};

/**
 * Runs a call-back booking and tracks the two things every confirmation screen shows: whether it
 * is in flight and whether it came back refused. Shared by both flows, which differ only in the
 * submit path they are handed.
 *
 * A refusal is reported to Sentry — the visitor is told to try again, and a call back that never
 * got booked is otherwise invisible to us.
 */
export function useCallbackSubmit(onConfirm?: ConfirmCallback): CallbackSubmit {
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);

  const submit = async (payload: CallbackPayload) => {
    setSubmitting(true);
    setFailed(false);

    try {
      await onConfirm?.(payload);

      return payload;
    } catch (error) {
      Sentry.captureException(error, { tags: { feature: 'callback-scheduler' } });
      setFailed(true);

      return null;
    } finally {
      setSubmitting(false);
    }
  };

  return { submitting, failed, submit };
}
